import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, userActionTypes, } from '@thoughtspot/metrics';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { getCustomRegionMaxFileSize } from '@thoughtspot/session-service';
import React, { useEffect, useMemo, useRef, useState, } from 'react';
import { BasePageContext } from "../../../../contexts/basepage-context";
import { convertBytesToMb, DATA_TEST_ID, getPropertyKeysFromTopojson, getSanitisedTopoJsoncontent, handleCustomRegionMutationError, } from "../../../../utils/chart-customization";
import { useCustomRegionUpdateMutation, useCustomRegionUploadMutation, useGetCustomRegionFromCacheQuery, } from '../../../../services/chart-customization/chart-customization-service';
import { create } from '../../../../utils/logger';
import { CustomRegion, FILE_FETCH_SOURCE } from './custom-region';
import styles from './custom-region.module.scss';
export const CUSTOM_REGION_MIME_TYPE = 'application/JSON';
export const CUSTOM_REGION_FILE_FETCH_ENDPOINT = '/callosum/v1/file/getfile/';
export const CustomRegionContainer = ({ selectedFileId, isOpen = false, onUpdate, onCancellation, dependentsData, currentOrgId, }) => {
    const { t } = useTranslation();
    const logger = create('geomap');
    const fileObject = useGetCustomRegionFromCacheQuery(selectedFileId);
    const [displayName, setDisplayName] = useState(fileObject?.name ?? '');
    const [customRegionFileSource, setCustomRegionFileSource] = useState(FILE_FETCH_SOURCE.LOCAL);
    const [customRegionLocalPath, setCustomRegionLocalPath] = useState('');
    const [customRegionRemotePath, setCustomRegionRemotePath] = useState('');
    const [customRegionFileContent, setCustomRegionFileContent] = useState(null);
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorField, setErrorField] = useState(null);
    const [sanitisedTopojson, setSanitisedTopojson] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [geometryType, setGeometryType] = useState(null);
    const regionsCount = useRef(0);
    const mapFileSize = useRef(0);
    const { metricsService } = BasePageContext();
    const handleError = (newErrorMessage = null, newErrorField = null) => {
        setErrorMessage(newErrorMessage);
        setErrorField(newErrorField);
        setIsMapLoading(false);
        if (newErrorField === customRegionFileSource)
            setCustomRegionFileContent(null);
    };
    const propertyKeys = useMemo(() => {
        if (customRegionFileContent) {
            setIsMapLoading(true);
            setSanitisedTopojson(null);
            const propertyKeyList = getPropertyKeysFromTopojson(customRegionFileContent, () => handleError(t('customRegion.file.invalid.error'), customRegionFileSource));
            if (propertyKeyList.length === 1) {
                setSelectedProperty(propertyKeyList[0]);
                return [];
            }
            setIsMapLoading(false);
            return propertyKeyList;
        }
        return [];
    }, [customRegionFileContent]);
    const fetchAndUpdateSanitisedTopojson = () => {
        const result = getSanitisedTopoJsoncontent(customRegionFileContent, selectedProperty, (hasDuplicate, isInvalid) => {
            setSanitisedTopojson(null);
            if (hasDuplicate) {
                if (propertyKeys?.length) {
                    handleError(t('customRegion.file.key.duplicate.regions.error'), DATA_TEST_ID.PROPERTY_SELECTOR);
                }
                else {
                    handleError(t('customRegion.file.duplicate.regions.error'), customRegionFileSource);
                }
            }
            if (isInvalid) {
                handleError(t('customRegion.file.invalid.error'), customRegionFileSource);
            }
        });
        setSanitisedTopojson(result);
    };
    useEffect(() => {
        let timeoutRef;
        if (selectedProperty && customRegionFileContent) {
            setIsMapLoading(true);
            handleError();
            timeoutRef = window.setTimeout(() => {
                fetchAndUpdateSanitisedTopojson();
            });
        }
        else {
            setSanitisedTopojson(null);
        }
        return () => {
            clearTimeout(timeoutRef);
        };
    }, [selectedProperty]);
    useEffect(() => {
        if (selectedFileId) {
            const controller = new AbortController();
            const signal = controller.signal;
            setIsMapLoading(true);
            fetch(`${CUSTOM_REGION_FILE_FETCH_ENDPOINT}${selectedFileId}`, {
                signal,
            })
                .then(async (response) => {
                const content = await response.json();
                setCustomRegionFileContent(JSON.stringify(content));
                return null;
            })
                .catch(err => {
                logger.warn('error in downloading file content', err);
                setErrorMessage(t('admin.error.generic'));
                setIsMapLoading(false);
            });
            return () => {
                controller.abort();
            };
        }
        return null;
    }, [selectedFileId]);
    const { error: uploadCustomRegionError, loading: uploadCustomRegionLoading, uploadCustomRegion, } = useCustomRegionUploadMutation();
    const { error: updateCustomRegionError, loading: updateCustomRegionLoading, updateCustomRegion, } = useCustomRegionUpdateMutation();
    useEffect(() => {
        if (updateCustomRegionError || uploadCustomRegionError) {
            handleCustomRegionMutationError(updateCustomRegionError || uploadCustomRegionError, handleError);
        }
    }, [updateCustomRegionError, uploadCustomRegionError]);
    const isFileSizeValid = (fileSizeInBytes) => {
        const customRegionMaxFileSize = getCustomRegionMaxFileSize();
        mapFileSize.current = convertBytesToMb(fileSizeInBytes);
        if (fileSizeInBytes > customRegionMaxFileSize) {
            handleError(t('customRegion.file.size.exceeded', [
                `${convertBytesToMb(customRegionMaxFileSize)} MB`,
            ]), customRegionFileSource);
            return false;
        }
        return true;
    };
    const handleLocalFileUpdate = (event) => {
        handleError();
        setIsMapLoading(true);
        if (event.target?.files?.length) {
            const file = event.target.files[0];
            if (!isFileSizeValid(file.size)) {
                return;
            }
            const reader = new FileReader();
            setCustomRegionLocalPath(file.name);
            reader.onload = (ev) => {
                setCustomRegionFileContent(ev.target.result?.toString());
            };
            reader.onerror = () => {
                handleError(t('customRegion.file.remote.invalid'), customRegionFileSource);
            };
            reader.readAsText(file, 'UTF-8');
        }
    };
    const handleRemoteFileDownload = async () => {
        setIsMapLoading(true);
        try {
            const result = await fetch(customRegionRemotePath, {
                method: 'HEAD',
            });
            const fileSizeInBytes = Number(result.headers.get('content-length'));
            if (!isFileSizeValid(fileSizeInBytes)) {
                return;
            }
            const response = await fetch(customRegionRemotePath);
            const jsonResponse = await response.json();
            setCustomRegionFileContent(JSON.stringify(jsonResponse));
        }
        catch (err) {
            handleError(t('customRegion.file.remote.invalid'), customRegionFileSource);
            logger.warn('error in downloading remote source', err);
        }
    };
    const handleOnSubmit = async () => {
        if (errorField)
            return;
        if (!displayName?.trim()) {
            setErrorField(DATA_TEST_ID.DISPLAY_NAME);
            setErrorMessage(t('customRegion.file.form.input.required'));
            return;
        }
        if (propertyKeys.length && !selectedProperty) {
            setErrorField(DATA_TEST_ID.PROPERTY_SELECTOR);
            setErrorMessage(t('customRegion.file.form.input.required'));
            return;
        }
        if (!selectedFileId &&
            (customRegionFileSource === FILE_FETCH_SOURCE.LOCAL
                ? !customRegionLocalPath
                : !customRegionRemotePath)) {
            setErrorField(customRegionFileSource);
            setErrorMessage(t('customRegion.file.form.input.required'));
            return;
        }
        if (!sanitisedTopojson) {
            setErrorField(customRegionFileSource);
            setErrorMessage(t('customRegion.file.invalid.error'));
            return;
        }
        if (!geometryType?.trim()) {
            setErrorField(DATA_TEST_ID.DISPLAY_NAME);
            setErrorMessage(t('customRegion.file.form.input.required'));
            return;
        }
        const metricProps = {
            source: customRegionFileSource,
            fileType: 'TopoJSON',
            noOfRegions: regionsCount.current,
            size: mapFileSize.current,
        };
        if (selectedFileId) {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.MAP_ADDED_OR_EDITED), {
                type: userActionTypes.ADDED_NEW_MAP.name,
                ...metricProps,
            });
            await updateCustomRegion(selectedFileId, sanitisedTopojson, CUSTOM_REGION_MIME_TYPE, displayName, geometryType, currentOrgId);
        }
        else {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.MAP_ADDED_OR_EDITED), {
                type: userActionTypes.EDITED_EXISTING_MAP.name,
                ...metricProps,
            });
            await uploadCustomRegion(sanitisedTopojson, CUSTOM_REGION_MIME_TYPE, displayName, geometryType, currentOrgId);
        }
        onUpdate(displayName);
    };
    const onFormUpdate = (element, value, event) => {
        handleError();
        switch (element) {
            case DATA_TEST_ID.DISPLAY_NAME:
                setDisplayName(value);
                break;
            case DATA_TEST_ID.SOURCE_PICKER:
                setCustomRegionFileSource(value);
                setCustomRegionLocalPath('');
                setCustomRegionRemotePath('');
                setCustomRegionFileContent(null);
                setSanitisedTopojson(null);
                break;
            case DATA_TEST_ID.FILE_PICKER:
                handleLocalFileUpdate(event);
                setSelectedProperty(null);
                break;
            case DATA_TEST_ID.URL_SOURCE:
                setCustomRegionRemotePath(value);
                setSelectedProperty(null);
                break;
            case DATA_TEST_ID.PROPERTY_SELECTOR:
                if (value === selectedProperty)
                    return;
                setIsMapLoading(true);
                setSelectedProperty(value);
                break;
            default:
                break;
        }
    };
    const handleRegionCountUpdate = (count) => {
        regionsCount.current = count;
        setIsMapLoading(false);
    };
    return (React.createElement(Modal, { isContentLoading: uploadCustomRegionLoading || updateCustomRegionLoading, title: selectedFileId
            ? t('chartCustomization.geomaps.customRegion.modal.editMap')
            : t('chartCustomization.geomaps.customRegion.modal.addMap'), isOpen: isOpen, size: ModalSize.LARGE, alwaysMaxHeight: true, autoScrollContent: false, confirmButton: {
            text: selectedFileId
                ? t('chartCustomization.geomaps.customRegion.modal.confirm.save')
                : t('chartCustomization.geomaps.customRegion.modal.confirm.add'),
            onConfirm: handleOnSubmit,
            isDisabled: uploadCustomRegionLoading || updateCustomRegionLoading,
        }, dismissButton: {
            text: t('chartCustomization.geomaps.customRegion.modal.cancel'),
            onClick: () => {
                onCancellation();
            },
            isDisabled: uploadCustomRegionLoading || updateCustomRegionLoading,
        }, contentClassName: styles.modalContainer },
        React.createElement(CustomRegion, { errorMessage: errorMessage ||
                uploadCustomRegionError?.message ||
                updateCustomRegionError?.message, errorField: errorField, handleError: handleError, isMapLoading: isMapLoading, handleRemoteFileDownload: handleRemoteFileDownload, customRegionFileSource: customRegionFileSource, customRegionFileContent: sanitisedTopojson, customRegionFileLocalPath: customRegionLocalPath, customRegionFileRemotePath: customRegionRemotePath, mapDisplayName: displayName, onFormUpdate: onFormUpdate, propertyKeys: propertyKeys, selectedProperty: selectedProperty, updateRegionsCount: handleRegionCountUpdate, updateGeometryType: setGeometryType, dependentsData: selectedFileId ? dependentsData : undefined })));
};
//# sourceMappingURL=custom-region.container.js.map